// src/context/CartContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';

const CartContext = createContext();

export const useCart = () => {
    return useContext(CartContext);
};

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);
    const [loadingCart, setLoadingCart] = useState(true);
    const { currentUser } = useAuth();

    useEffect(() => {
        const loadCart = async () => {
            if (currentUser) {
                const cartRef = doc(db, 'carts', currentUser.uid);
                const cartSnap = await getDoc(cartRef);
                if (cartSnap.exists()) {
                    setCartItems(cartSnap.data().items || []);
                } else {
                    const localCart = localStorage.getItem('localCart');
                    if (localCart) {
                        const parsedLocalCart = JSON.parse(localCart);
                        setCartItems(parsedLocalCart);
                        await setDoc(cartRef, { items: parsedLocalCart });
                        localStorage.removeItem('localCart');
                    } else {
                        setCartItems([]);
                    }
                }
            } else {
                const localCart = localStorage.getItem('localCart');
                setCartItems(localCart ? JSON.parse(localCart) : []);
            }
            setLoadingCart(false);
        };
        loadCart();
    }, [currentUser]);

    useEffect(() => {
        const saveCart = async () => {
            if (!loadingCart) {
                if (currentUser) {
                    const cartRef = doc(db, 'carts', currentUser.uid);
                    await setDoc(cartRef, { items: cartItems }, { merge: true });
                } else {
                    localStorage.setItem('localCart', JSON.stringify(cartItems));
                }
            }
        };
        saveCart();
    }, [cartItems, currentUser, loadingCart]);

    const addToCart = (product, quantity = 1) => {
        setCartItems(prev => {
            const exist = prev.find(item => item.id === product.id);
            if (exist) {
                return prev.map(item => item.id === product.id ? { ...item, quantity: item.quantity + quantity } : item);
            }
            return [...prev, { ...product, quantity }];
        });
    };
    const removeFromCart = (productId) => setCartItems(prev => prev.filter(item => item.id !== productId));
    const updateQuantity = (productId, newQuantity) => {
        const qty = parseInt(newQuantity, 10);
        if (qty < 1) return removeFromCart(productId);
        setCartItems(prev => prev.map(item => item.id === productId ? { ...item, quantity: qty } : item));
    };
    const clearCart = () => setCartItems([]);

    const value = { cartItems, addToCart, removeFromCart, updateQuantity, clearCart, cartCount: cartItems.reduce((acc, item) => acc + item.quantity, 0) };

    return <CartContext.Provider value={value}>{!loadingCart && children}</CartContext.Provider>;
};